import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import BreadcrumbItem from './BreadcrumbItem';

const defaultItems = [{ label: 'Furniture', link: '/shop' }];

const Breadcrumb = ({
  color = 'dark',
  forPage,
  items: initialItems,
  layout = '',
  loading = false,
  noPadding = false,
}) => {
  const items = initialItems ?? defaultItems;
  const pageBreadcrumbRef = useRef(null);

  const desktopItems = useMemo(
    () =>
      forPage === 'CategoryDesktop' && items.length >= 2
        ? items.slice(1, items.length - 1)
        : items,
    [forPage, items]
  );

  useEffect(() => {
    if (forPage && ['PDPMobile', 'CategoryMobile'].includes(forPage)) {
      const element = pageBreadcrumbRef.current;

      if (element) {
        if (element.scrollWidth > element.clientWidth) {
          // there is overflow of text.change direction to rtl.
          element.style.direction = 'rtl';
        } else {
          element.style.direction = 'ltr';
        }
      }
    }
  }, [forPage, items]);

  return (
    <div
      className={classNames('z-auto m-0 mt-0 pt-2 pl-5', {
        'min-h-[45px]': !noPadding,
        'min-h-[auto]': noPadding,
        static: layout !== 'float',
        'absolute top-2.5 left-0 max-[1023px]:relative max-[1023px]:top-0':
          layout === 'float',
        'bg-gray-light9 border-gray-light7 border-b border-t md:pl-[50px]':
          color !== 'light',
        'bg-transparent border-0 [&_a]:text-gray-light1 [&_li:last-child_span]:last:text-gray-light1 [&_a:hover]:text-white sm:pt-0 sm:pl-0 md:mt-2':
          color === 'light',
      })}
      ref={pageBreadcrumbRef}
    >
      <ol
        className={classNames('list-none text-xs', {
          'm-0 p-0 lg:m-0': noPadding,
        })}
        itemScope
        itemType="https://schema.org/BreadcrumbList"
      >
        {desktopItems.map((item, index) => (
          <BreadcrumbItem
            loading={loading}
            key={item.label}
            isLast={index === items.length - 1}
            index={index}
            {...item}
          />
        ))}
      </ol>
    </div>
  );
};

Breadcrumb.propTypes = {
  color: PropTypes.oneOf(['light', 'dark']),
  layout: PropTypes.string,
  loading: PropTypes.bool,
  forPage: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.string,
      label: PropTypes.string.isRequired,
    })
  ),
  noPadding: PropTypes.bool,
};

export const BreadcrumbPlaceholder = props => (
  <Breadcrumb
    {...props}
    loading
    layout={props.layout}
    items={
      props.forPage === 'CategoryDesktop'
        ? [{ label: '1' }]
        : [{ label: '1' }, { label: '2' }, { label: '3' }]
    }
  />
);

BreadcrumbPlaceholder.propTypes = {
  forPage: PropTypes.string,
  layout: PropTypes.string,
};

export default Breadcrumb;
